import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  Box,
  Stack,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  MenuItem,
  Menu,
  Pagination,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Loading from '../../components/loading';
import { usePatients } from '../hooks/usePatients';
import { useDeletePatient } from '../hooks/useDeletePatient';
import SuccessMessage from '../../components/successMessage';
import ErrorMessage from '../../components/errorMessage';

export default function PatientList({ onPatientSelect }) {
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [actualPage, setActualPage] = useState(1);
  const [patientNameOrCpf, setPatientNameOrCpf] = useState('');

  const [showModalToDeletePatient, setShowModalToDeletePatient] = useState(false);
  const [patientToDelete, setPatientToDelete] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);

  const navigate = useNavigate();

  const {
    patients: allPatients,
    paginationCount,
    isLoading,
    error: listError,
    fetchAll: getAllPatients,
    fetchByPage,
    searchByNameOrCpf,
  } = usePatients();
  const { remove } = useDeletePatient();

  useEffect(() => {
    if (listError) {
      setShowErrorMessage(true);
      setErrorMessage(listError);
    }
  }, [listError]);

  function findByPage(page) {
    fetchByPage(page, patientNameOrCpf);
  }

  function findByNameOrCpf() {
    if (!patientNameOrCpf) {
      getAllPatients();
      return;
    }
    searchByNameOrCpf(patientNameOrCpf);
  }

  const openMenu = (event, patient) => {
    setAnchorEl(event.currentTarget);
    setSelectedPatient(patient);
  };

  const closeMenu = () => {
    setAnchorEl(null);
    setSelectedPatient(null);
  };

  const openModalToDeletePatient = (patient) => {
    setPatientToDelete(patient);
    setShowModalToDeletePatient(true);
    closeMenu();
  };

  const closeModalToDeletePatient = () => {
    setShowModalToDeletePatient(false);
    setPatientToDelete(null);
    setSelectedPatient(null);
  };

  const deletePatient = () => {
    if (!patientToDelete) return;
    setShowModalToDeletePatient(false);
    remove(patientToDelete.id)
      .then((returnFromApi) => {
        findByPage(actualPage);
        setShowSuccessMessage(true);
        setSuccessMessage(returnFromApi.data);
      })
      .catch((returnFromApi) => {
        console.error('Ops! Ocorreu um erro ao deletar o paciente:', returnFromApi.response);
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
      })
      .finally(() => {
        closeModalToDeletePatient();
      });
  };

  const handleNavigateToCreate = () => {
    navigate('/patient-form');
  };

  const searchPatient = (event) => {
    event.preventDefault();
    findByNameOrCpf();
  };

  const changePage = (event, value) => {
    event.preventDefault();
    setActualPage(value);
    findByPage(value);
  };

  function goToMedicalRecord(patient) {
    navigate(`/patient-form/${patient.id}`);
  }

  return (
    <Card sx={{ borderRadius: '1rem', boxShadow: 3 }}>
      <CardHeader
        title={
          <Typography variant="h5" sx={{ mb: 0, fontWeight: 'bold', color: 'text.secondary' }}>
            Pacientes
          </Typography>
        }
        sx={{ borderBottom: 0, py: 3 }}
      />
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button data-cy="new-patient-btn" variant="outlined" onClick={handleNavigateToCreate}>
            Novo
          </Button>
        </Box>

        <Typography variant="h6" sx={{ mb: 1 }}>
          Buscar Paciente
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
          <TextField
            data-cy="patient-search"
            fullWidth
            placeholder="Digite o nome ou CPF e pressione Enter"
            value={patientNameOrCpf}
            onChange={(e) => setPatientNameOrCpf(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && searchPatient(e)}
          />
          <Button
            data-cy="find-patient-btn"
            variant="contained"
            color="primary"
            onClick={searchPatient}
          >
            Buscar
          </Button>
        </Stack>

        <Loading isToHidden={!isLoading} />
        {showSuccessMessage && (
          <SuccessMessage
            message={successMessage}
            show={showSuccessMessage}
            setShow={setShowSuccessMessage}
          />
        )}
        {showErrorMessage && (
          <ErrorMessage
            message={errorMessage}
            show={showErrorMessage}
            setShow={setShowErrorMessage}
          />
        )}

        <List>
          {allPatients.length > 0 ? (
            allPatients.map((patient) => (
              <ListItem
                data-cy="patient-row"
                key={patient.id}
                onClick={() => goToMedicalRecord(patient)}
                sx={{
                  mt: 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  p: 1,
                  boxShadow: 1,
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
              >
                <ListItemText primary={patient.name} secondary={`CPF: ${patient.cpf}`} />

                <Box>
                  <Button
                    data-cy="patient-actions-btn"
                    aria-controls={anchorEl ? 'patient-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={anchorEl ? 'true' : undefined}
                    onClick={(event) => {
                      event.stopPropagation();
                      openMenu(event, patient);
                    }}
                  >
                    <MoreVertIcon />
                  </Button>
                  <Menu
                    id="patient-menu"
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl) && selectedPatient?.id === patient.id}
                    onClose={closeMenu}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MenuItem
                      data-cy="medical-record-patient-btn"
                      onClick={() => goToMedicalRecord(patient)}
                    >
                      Ir para o Prontuário
                    </MenuItem>
                    <MenuItem
                      data-cy="delete-patient-btn"
                      onClick={() => openModalToDeletePatient(patient)}
                    >
                      Excluir
                    </MenuItem>
                  </Menu>
                </Box>
              </ListItem>
            ))
          ) : (
            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Typography>Nenhum paciente encontrado.</Typography>
            </Box>
          )}
        </List>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={paginationCount}
            page={actualPage}
            onChange={changePage}
            variant="outlined"
            shape="rounded"
          />
        </Box>
      </CardContent>

      <Dialog
        open={showModalToDeletePatient}
        onClose={closeModalToDeletePatient}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{'Confirmar exclusão'}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Tem certeza de que deseja deletar o paciente "{patientToDelete?.name}"?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModalToDeletePatient}>Cancelar</Button>
          <Button
            data-cy="confirm-delete-patient-btn"
            onClick={deletePatient}
            sx={{ color: 'error.main' }}
          >
            Deletar
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
